import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';
import { SORT_DIRECTIONS, type SortDirection } from '@/components/common/DataTable';
import { optionalPositiveIdSchema, optionalTextSchema } from '@/utils/searchParams';
import {
  ACTIVITY_STATUS,
  DCR_APPROVAL_STATUS,
  DEFAULT_MAP_ON,
  MAP_ON_OPTIONS,
  MO_TRAVEL_SORT_COLUMNS,
  type ActivityStatus,
  type MapOn,
  type MoTravelSortColumn,
} from '../constants/moTravel.constants';
import { getBangladeshToday } from '../utils/bangladeshClock';

const wallClockSchema = z.string().nullable();

const moTravelListItemSchema = z.object({
  amUserId: z.coerce.number().nullable(),
  amName: z.string().nullable(),
  userId: z.coerce.number(),
  moName: z.string(),
  hrId: z.string().nullable(),
  division: z.string().nullable(),
  zilla: z.string().nullable(),
  thana: z.string().nullable(),
  startTime: wallClockSchema,
  lastActivity: wallClockSchema,
  totalEntry: z.number(),
  averageSpendMinutes: z.number().nullable(),
  activityStatus: z.enum(ACTIVITY_STATUS),
});

export const paginatedMoTravelSchema = z.object({
  data: z.array(moTravelListItemSchema),
  meta: paginationMetaSchema,
});

export const moTravelStatsSchema = z.object({
  officerCount: z.number(),
  submittedCount: z.number(),
  noLocationCount: z.number(),
  totalEntry: z.number(),
});

const moTravelProfileSchema = z.object({
  userId: z.coerce.number(),
  fullName: z.string(),
  username: z.string(),
  hrId: z.string().nullable(),
  designation: z.string().nullable(),
  department: z.string().nullable(),
  division: z.string().nullable(),
  zilla: z.string().nullable(),
  thana: z.string().nullable(),
  amUserId: z.coerce.number().nullable(),
  amName: z.string().nullable(),
});

const moTravelSummarySchema = z.object({
  totalEntry: z.number(),
  startTime: wallClockSchema,
  lastActivity: wallClockSchema,
  totalMinutes: z.number().nullable(),
  routeKm: z.number(),
  institutesCovered: z.number(),
  totalBill: z.number(),
});

const moTravelTransportTypeSchema = z.object({
  code: z.string().nullable(),
  name: z.string(),
});

const moTravelStopSchema = z.object({
  seq: z.number(),
  reference: z.string(),
  dcrId: z.coerce.number(),
  legacyId: z.coerce.number(),
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  name: z.string().nullable(),
  hasLocation: z.boolean(),
  timeFrom: wallClockSchema,
  timeTo: wallClockSchema,
  submittedAt: wallClockSchema,
  spendMinutes: z.number().nullable(),
  instituteId: z.coerce.number().nullable(),
  instituteName: z.string().nullable(),
  dcrTypeCode: z.string().nullable(),
  approvalStatus: z.enum(DCR_APPROVAL_STATUS),
  transportType: moTravelTransportTypeSchema.nullable().default(null),
});

const moTravelRoadLegSchema = z.object({
  path: z.array(z.tuple([z.number(), z.number()])).nullable(),
  km: z.number(),
});

const moTravelDaySchema = z.object({
  date: z.string(),
  startTime: wallClockSchema,
  lastActivity: wallClockSchema,
  totalEntry: z.number(),
  totalMinutes: z.number().nullable(),
  institutesCovered: z.number(),
  totalBill: z.number(),
  routeKm: z.number(),
  start: z.object({ lat: z.number(), lon: z.number(), name: z.string().nullable() }).nullable(),
  stops: z.array(moTravelStopSchema),
  roadLegs: z.array(moTravelRoadLegSchema).nullable().default(null),
});

export const moTravelRouteSchema = z.object({
  profile: moTravelProfileSchema,
  summary: moTravelSummarySchema,
  days: z.array(moTravelDaySchema),
});

export type MoTravelListItem = z.infer<typeof moTravelListItemSchema>;
export type PaginatedMoTravel = z.infer<typeof paginatedMoTravelSchema>;
export type MoTravelStats = z.infer<typeof moTravelStatsSchema>;
export type MoTravelSummary = z.infer<typeof moTravelSummarySchema>;
export type MoTravelTransportType = z.infer<typeof moTravelTransportTypeSchema>;
export type MoTravelStop = z.infer<typeof moTravelStopSchema>;
export type MoTravelRoadLeg = z.infer<typeof moTravelRoadLegSchema>;
export type MoTravelDay = z.infer<typeof moTravelDaySchema>;
export type MoTravelRoute = z.infer<typeof moTravelRouteSchema>;

export interface MoTravelPeriodParams {
  date: string;
  mapOn: MapOn;
}

export interface MoTravelFilterParams extends MoTravelPeriodParams {
  userTypeId?: number | undefined;
  companyId?: number | undefined;
  departmentId?: number | undefined;
  designationId?: number | undefined;
  hrId?: string | undefined;
  username?: string | undefined;
  mobile?: string | undefined;
  fullName?: string | undefined;
}

export interface MoTravelListParams extends MoTravelFilterParams {
  activityStatus?: ActivityStatus | undefined;
  sortBy?: MoTravelSortColumn | undefined;
  sortDirection?: SortDirection | undefined;
  pageSize: number;
}

export interface WiderPeriodAction {
  label: string;
  onClick: () => void;
}

const isoDateSchema = z.iso.date();

const moTravelPeriodSearchSchema = z.object({
  date: isoDateSchema.catch(getBangladeshToday),
  mapOn: z.enum(MAP_ON_OPTIONS).catch(DEFAULT_MAP_ON),
});

export const moTravelListSearchSchema = moTravelPeriodSearchSchema.extend({
  userTypeId: optionalPositiveIdSchema,
  companyId: optionalPositiveIdSchema,
  departmentId: optionalPositiveIdSchema,
  designationId: optionalPositiveIdSchema,
  hrId: optionalTextSchema,
  username: optionalTextSchema,
  mobile: optionalTextSchema,
  fullName: optionalTextSchema,
  activityStatus: z.enum(ACTIVITY_STATUS).optional().catch(undefined),
  sortBy: z.enum(MO_TRAVEL_SORT_COLUMNS).optional().catch(undefined),
  sortDirection: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
});

export const moTravelDetailSearchSchema = moTravelPeriodSearchSchema.extend({
  day: isoDateSchema.optional().catch(undefined),
});

export type MoTravelListSearch = z.infer<typeof moTravelListSearchSchema>;
